import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { AdImageCropDialog } from "../../components/admin/AdImageCropDialog";
import { AD_IMAGE_HEIGHT, AD_IMAGE_WIDTH } from "../../lib/ads";
import { fileToDataUrl } from "../../lib/imageCrop";

const INITIAL = {
  headline: "",
  stubLabel: "AFFILIATE AD",
  href: "",
  keyword: "",
  adType: "EMERGENT",
  active: true,
  image: null,
};

export default function AdminAdCreate() {
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";
  const navigate = useNavigate();

  const [form, setForm] = React.useState(INITIAL);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [cropSource, setCropSource] = React.useState("");
  const [cropSourceName, setCropSourceName] = React.useState("");
  const [cropSourceType, setCropSourceType] = React.useState("image/jpeg");
  const [cropOpen, setCropOpen] = React.useState(false);

  React.useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const setPreview = (nextUrl) => {
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(nextUrl || "");
  };

  const onImagePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const imageDataUrl = await fileToDataUrl(file);
      setCropSource(imageDataUrl);
      setCropSourceName(file.name || "ad-image");
      setCropSourceType(file.type || "image/jpeg");
      setCropOpen(true);
      setError("");
    } catch (err) {
      setError(err?.message || "Unable to open image cropper.");
    }
  };

  const onCropCancel = () => {
    setCropOpen(false);
    setCropSource("");
    setCropSourceName("");
    setCropSourceType("image/jpeg");
  };

  const onCropConfirm = async (croppedFile) => {
    setForm((p) => ({ ...p, image: croppedFile }));
    setPreview(URL.createObjectURL(croppedFile));
    onCropCancel();
  };

  const clearSelectedImage = () => {
    setForm((p) => ({ ...p, image: null }));
    setPreview("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!passcode) {
      setError("Missing passcode. Open this page from /admin.");
      return;
    }
    if (!form.image) {
      setError("Please upload an image for this ad.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const { createAdminAd } = await import("../../lib/api");
      const fd = new FormData();
      fd.append("headline", form.headline);
      fd.append("stubLabel", form.stubLabel);
      fd.append("href", form.href);
      fd.append("keyword", form.keyword);
      fd.append("adType", form.adType.toUpperCase());
      fd.append("active", String(!!form.active));
      fd.append("image", form.image);
      await createAdminAd(fd, passcode);
      navigate(`/admin/ads?passcode=${encodeURIComponent(passcode)}`);
    } catch (_) {
      setError("Failed to create ad. Check the form values and try again.");
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10" data-testid="admin-ad-create-page">
      <Card className="border-cyan-400/30 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 shadow-[0_0_35px_rgba(34,211,238,0.14)]">
        <CardHeader>
          <CardTitle className="text-slate-100" data-testid="admin-ad-create-title">Create Ad</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-ad-create-form">
            {!passcode && <div className="text-sm text-rose-300" data-testid="admin-ad-create-missing-passcode">Missing passcode. Go back to /admin.</div>}
            {error && <div className="text-sm text-rose-300" data-testid="admin-ad-create-error">{error}</div>}

            <div className="space-y-1">
              <label className="text-xs text-slate-300" htmlFor="ad-headline">Internal ad name (admin only)</label>
              <input id="ad-headline" value={form.headline} onChange={(e) => setForm((p) => ({ ...p, headline: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="Used in admin list/search" required data-testid="admin-ad-create-headline" />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-slate-300" htmlFor="ad-stub-label">Ad stub tab label (shown above ad)</label>
              <input id="ad-stub-label" value={form.stubLabel} onChange={(e) => setForm((p) => ({ ...p, stubLabel: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="AFFILIATE AD or AD" required data-testid="admin-ad-create-stub-label" />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-slate-300" htmlFor="ad-link">Ad link destination</label>
              <input id="ad-link" type="url" value={form.href} onChange={(e) => setForm((p) => ({ ...p, href: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="https://..." required data-testid="admin-ad-create-link" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs text-slate-300" htmlFor="ad-type">Ad type</label>
                <select id="ad-type" value={form.adType} onChange={(e) => setForm((p) => ({ ...p, adType: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" data-testid="admin-ad-create-type">
                  <option value="EMERGENT">EMERGENT</option>
                  <option value="DONATION">DONATION</option>
                  <option value="AMAZON">AMAZON</option>
                  <option value="AFFILIATE">AFFILIATE</option>
                  <option value="GENERAL">GENERAL</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300" htmlFor="ad-keyword">Keyword (search tag)</label>
                <input id="ad-keyword" value={form.keyword} onChange={(e) => setForm((p) => ({ ...p, keyword: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="focus, supplement, etc" required data-testid="admin-ad-create-keyword" />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-slate-300" htmlFor="ad-image">Ad image</label>
              <input id="ad-image" type="file" accept="image/*" onChange={onImagePicked} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" data-testid="admin-ad-create-image" />
              <div className="text-xs text-slate-400" data-testid="admin-ad-create-image-specs">Crop output: {AD_IMAGE_WIDTH} × {AD_IMAGE_HEIGHT}px</div>
              {previewUrl ? (
                <div className="space-y-2" data-testid="admin-ad-create-image-preview-wrap">
                  <div className="overflow-hidden rounded-2xl border border-cyan-300/30 bg-black/50" style={{ aspectRatio: `${AD_IMAGE_WIDTH} / ${AD_IMAGE_HEIGHT}` }}>
                    <img src={previewUrl} alt="Ad preview" className="h-full w-full object-cover" data-testid="admin-ad-create-image-preview" />
                  </div>
                  <button type="button" onClick={clearSelectedImage} className="rounded-full border border-slate-600 px-3 py-1.5 text-xs" data-testid="admin-ad-create-image-clear">
                    Remove selected image
                  </button>
                </div>
              ) : null}
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-slate-200" data-testid="admin-ad-create-active-toggle">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
              Active in rotation after creation
            </label>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              <button type="button" onClick={() => navigate(`/admin/ads?passcode=${encodeURIComponent(passcode)}`)} className="rounded-full border border-slate-600 px-4 py-2 text-sm" data-testid="admin-ad-create-cancel">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="rounded-full border border-cyan-300/50 bg-cyan-500/15 px-4 py-2 text-sm font-semibold text-cyan-100" data-testid="admin-ad-create-submit">
                {saving ? "Creating..." : "Create ad"}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      <AdImageCropDialog
        open={cropOpen}
        imageSrc={cropSource}
        sourceFileName={cropSourceName}
        sourceMimeType={cropSourceType}
        onCancel={onCropCancel}
        onConfirm={onCropConfirm}
      />
    </div>
  );
}